import { useEffect, useContext, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { api } from "/src/redux/api";
import Split from "react-split";
import TemplatePropertiesContainer from "/src/components/TemplateProperties/TemplatePropertiesContainer";
import OutboundPropertiesContainer from "/src/components/ItemProperties/OutboundPropertiesContainer";
import WorkflowGroupPanel from "/src/components/Panel/WorkflowGroupPanel";
import OutboundTemplatePanel from "/src/components/Panel/OutboundTemplatePanel ";
import { treeActions } from "../../../redux/slices/treeSlice";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { cloneDeep } from "lodash";
import { getDataDepotObject } from "../../../utils/utilFunctions";
import DocPanel from "../../../components/Panel/DocPanel";
import MappingPropertiesContainer from "../../../components/ItemProperties/MappingPropertiesContainer";
// import MappingConnections from "../../../components/Mapping/MappingConnections";
import SimpleMappingConnections from "../../../components/Mapping/SimpleMappingConnections";
import MappingNotification from "../../../components/Mapping/MappingNotification";
import { validateMapping, findAutomaticMappings, findCrossTabAutomaticMappings } from "../../../utils/autoMapping";
import { eachDeep } from "deepdash-es/standalone";
import { t } from "i18next";

function Outbound(props) {
	const parentScreen = useContext(ParentScreenCtx);
	const dispatch = useDispatch();
	const { account, user, baseUrl, token } = useSelector(
		(state) => state.queryParams
	);
	const { showDocFile } = useSelector((state) => state.treeData.outbound);
	const treeData = useSelector((state) => state.treeData);
	const currentTab = useSelector(
		(state) => state.treeData[parentScreen].rightTree.currentTab
	);
	const selectedNode = useSelector(
		(state) => state.treeData[parentScreen].leftTree.tree.body.selectedNode
	);
	const rightSelectedNode = useSelector(
		(state) => {
			const currentTabFromState = state.treeData[parentScreen].rightTree.currentTab;
			return state.treeData[parentScreen].rightTree.tree[currentTabFromState]?.selectedNode || {};
		}
	);
	const isMappingMode = useSelector(state => state.treeData.mappingStates.isMappingMode);
	const isLinkMode = useSelector(state => state.treeData.mappingStates.isLinkMode);
	const [middleSelectedNode, setMiddleSelectedNode] = useState({});
	const [middleCurrTab, setMiddleCurrTab] = useState(1);
	
	// Ref to track tree structure changes (not mapping changes)
	const treeStructureRef = useRef(null);
	const inboundDependencyTree = useSelector(
		(state) =>
			state.treeData.outbound.leftTree.inboundDependencyTree
	);
	const rightTree = useSelector(
		(state) => {
			const currentTabFromState = state.treeData[parentScreen].rightTree.currentTab;
			return state.treeData[parentScreen].rightTree.tree[currentTabFromState]?.translatedData || [];
		}
	);

	const submitHandler = (key, value, bulk, destinationNode) => {
		const treeData = cloneDeep(
			rightTree
		);
		let found = false;
		eachDeep(treeData, (obj) => {
			if (found) return;
			if (obj?.key && obj?.key === destinationNode?.key) {
				found = true;
				if (!key) {
					Object.keys(bulk).forEach((e) => {
						obj[e] = bulk[e];
					});
				} else obj[key] = value;
				if (obj?.hasOwnProperty("AI_CONFIDENCE")) delete obj.AI_CONFIDENCE;
				if (obj?.FIELDTYPE === "CONSTANT") {
					obj.FIELDTYPE = "ALPHA";
				}
				if (["ATTRIBUTE"].includes(obj.ICON_DISPLAY)) {
					obj.title = `${obj?.ATTRIBUTE_NAME || bulk.MAPNAME || "UNNAMED"}`;
				}
				else {
					obj.title = `${obj?.ICON_DISPLAY?.includes("ELEMENT") ? "<" : ""}${!["ELEMENT"].includes(obj.ICON_DISPLAY) ? t("LBL_FIELD") : ""} ${obj?.ELEMENT_NAME || obj?.DESCRIPTION || bulk.MAPNAME || "UNNAMED"}${obj?.ICON_DISPLAY?.includes("ELEMENT") ? ">" : ""}`;
				}
				dispatch(
					treeActions.setRightTreeSelectedNode({
						screen: parentScreen,
						type: currentTab, // This is already correct as it's from Redux state
						data: obj,
					})
				);
			}
		});
		dispatch(
			treeActions.setRightTreeTransData({
				screen: parentScreen,
				type: currentTab, // This is already correct as it's from Redux state
				data: treeData,
			})
		);
	};
	
	const saveMappingToRedux = (sourceKey, targetKey, sourceTabOverride = null) => {
		// Only allow mapping creation when both mapping mode and link mode are active
		if (!isMappingMode || !isLinkMode) {
			console.warn('Mapping creation attempted when link mode is disabled');
			return;
		}
		
		const tabMap = {
			0: "header",
			1: "body",
			2: "trailer"
		}
		
		// Use sourceTabOverride if provided (for cross-tab mappings), otherwise use current tab
		const actualSourceTab = sourceTabOverride !== null ? sourceTabOverride : middleCurrTab;
		const sourceNode = findNode(inboundDependencyTree[tabMap[actualSourceTab]], sourceKey);
		const destinationNode = findNode(rightTree, targetKey);
		
		if (sourceNode?.hasOwnProperty("key") && destinationNode?.hasOwnProperty("key")) {
			// Validate the mapping before saving with tab restrictions
			const validation = validateMapping(sourceNode, destinationNode, false, 'left-to-right', false, actualSourceTab, currentTab);
			if (!validation.isValid) {
				// You can add toast notification here if needed
				console.error('Mapping validation failed:', validation.errorMessage);
				return;
			}
			
			// Save the mapping data to the target node
			submitHandler(null, null, {
				PARENT: sourceNode.PARENT,
				RECORD_TYPE: sourceNode.RECORD_TYPE,
				MAPNAME: sourceNode.MAPNAME,
			}, destinationNode);
			
			// Remove any existing mappings to this target node first
			dispatch(treeActions.removeMappingsByTargetKey({
				targetKey: targetKey,
				sourceTab: actualSourceTab
			}));
			
			// Also add the mapping to Redux state for line display
			const mappingData = {
				sourceKey: sourceKey,
				targetKey: targetKey,
				sourceTab: actualSourceTab,
				mappingType: 'left-to-right',
				isAutomatic: false
			};
			dispatch(treeActions.addMapping(mappingData));
			
			// Set the selected source node so the mapping line is visible
			const selectedNodeData = {
				tab: tabMap[actualSourceTab],
				selectedNode: sourceNode
			};
			dispatch(treeActions.setMappingSelectedSourceNode(selectedNodeData));
		}
	}


	// On screen mount (or account/dispatch props are updated) make a request to get the inbound template list
	useEffect(() => {
		// Add a subscription to endpoint
		const copySelecteNode = cloneDeep(rightSelectedNode);
		const tab = currentTab;
		dispatch(
			api.endpoints.getOutboundTemplateList.initiate(
				{ account },
				{ 
					subscribe: false,
					forceRefetch: true
				}
			)
		);
		dispatch(
			treeActions.setLeftTreeContextActionValue({
				screen: parentScreen,
				data: {
					name: selectedNode.title,
					workflowGroup: selectedNode.workflowGroup,
					tabChanged: true,
				},
			})
		);
		dispatch(
			treeActions.setRightTreeSelectedNode({
				screen: parentScreen,
				type: tab,
				data: copySelecteNode,
			})
		);
		dispatch(
			api.endpoints.getPdcList.initiate({
				account,
				direction: "out",
				datatype: "database",
			})
		);
		if (!treeData.dataDepots) {
			getDataDepotObject({ user, account, baseUrl, token }, (obj) => {
				dispatch(treeActions.setDataDepotObj(obj));
			});
		}
	}, [dispatch, account, props.val]); //eslint-disable-line react-hooks/exhaustive-deps

	// --- Cross-tab auto-mapping generation ---
	useEffect(() => {
		// Only run if both trees are loaded and mapping mode is enabled
		if (!inboundDependencyTree || !treeData.outbound.rightTree.tree || !isMappingMode) return;

		// Check if this is the initial load or if trees have actually changed
		// We don't want to re-run automatic mapping when manual mappings are added
		const currentTreeStructure = JSON.stringify({
			inbound: inboundDependencyTree,
			outbound: treeData.outbound.rightTree.tree
		});
		
		// Only run automatic mapping if tree structure has actually changed
		if (treeStructureRef.current === currentTreeStructure) {
			return; // Tree structure hasn't changed, skip automatic mapping
		}
		treeStructureRef.current = currentTreeStructure;

		// Use requestIdleCallback for non-critical mapping generation
		const generateMappings = () => {
			const tabNames = ['header', 'body', 'trailer'];
			let allAutoMappings = [];
			

			
			// Same-tab mappings (most common and efficient)
			for (let tabIndex = 0; tabIndex < 3; tabIndex++) {
				const sourceTree = inboundDependencyTree[tabNames[tabIndex]];
				const targetTree = treeData.outbound.rightTree.tree[tabNames[tabIndex]].translatedData;
				if (sourceTree && targetTree) {
					const mappings = findAutomaticMappings(sourceTree, targetTree, tabIndex);
					mappings.forEach(m => m.targetTab = tabIndex);
					allAutoMappings = allAutoMappings.concat(mappings);
				}
			}
			
			// Cross-tab mappings using optimized algorithm
			const crossTabMappings = findCrossTabAutomaticMappings(
				inboundDependencyTree, 
				treeData.outbound.rightTree.tree
			);
			
			// Filter out same-tab mappings and add only cross-tab ones
			const onlyCrossTabMappings = crossTabMappings.filter(mapping => 
				mapping.isCrossTab && mapping.sourceTab !== mapping.targetTab
			);
			
			allAutoMappings = allAutoMappings.concat(onlyCrossTabMappings);
			
			// Right-to-right mappings (same tab within right tree)
			for (let tabIndex = 0; tabIndex < 3; tabIndex++) {
				const rightTreeData = treeData.outbound.rightTree.tree[tabNames[tabIndex]].translatedData;
				if (rightTreeData && rightTreeData.length > 0) {
					const rightToRightMappings = findAutomaticMappings(rightTreeData, rightTreeData, tabIndex, true);
					rightToRightMappings.forEach(m => {
						m.targetTab = tabIndex;
						m.mappingType = 'right-to-right';
					});
					allAutoMappings = allAutoMappings.concat(rightToRightMappings);
				}
			}
			
			dispatch(treeActions.setAutomaticMappings(allAutoMappings));
		};

		// Use requestIdleCallback if available, otherwise use setTimeout
		if (window.requestIdleCallback) {
			window.requestIdleCallback(generateMappings, { timeout: 1000 });
		} else {
			setTimeout(generateMappings, 100);
		}
	}, [
		isMappingMode, 
		inboundDependencyTree, 
		treeData.outbound.rightTree.tree.body.translatedData, 
		treeData.outbound.rightTree.tree.header.translatedData, 
		treeData.outbound.rightTree.tree.trailer.translatedData, 
		dispatch
	]); //eslint-disable-line react-hooks/exhaustive-deps
	// --- End cross-tab auto-mapping generation ---

	const isDragging = useSelector(state => state.treeData.mappingStates.isDragging || false);
	
	return (
		<div className={`wbb-tab-container ${isMappingMode ? 'mapping-mode' : ''} ${isDragging ? 'dragging' : ''}`}>
			{/* three horiz. panels split */}
			<Split
				className='split height-100 width-100 flex'
				direction='horizontal'
				gutterSize={4}
				snapOffset={0}
				dragInterval={4}
				sizes={[34, 66]}
			>
				{/* left panel- wfg tree and template properties vert. split*/}
				<div className='height-100'>
					<Split
						style={{ height: "100%" }}
						className='split'
						direction='vertical'
						gutterSize={4}
						snapOffset={0}
						dragInterval={4}
						minSize={[100, 100]}
						maxSize={[500, 800]}
					>
						<WorkflowGroupPanel />
						<TemplatePropertiesContainer />
					</Split>
				</div>
				{showDocFile ? <DocPanel /> : null}
				{!showDocFile && (
					<div style={{ position: 'relative', height: '100%', width: '100%' }}>
						<Split
							gutterSize={4}
							snapOffset={0}
							className='split height-100 width-100 flex'
							direction='horizontal'
							sizes={[50, 50]}
						>
							<div className='height-100'>
								{isMappingMode ? <MappingPropertiesContainer 
									setMiddleSelectedNode={setMiddleSelectedNode} 
									currTab={middleCurrTab}
									setCurrTab={setMiddleCurrTab}
								/> : <OutboundPropertiesContainer />}
							</div>
							<div id='rightTreePanel' className='height-100'>
								<OutboundTemplatePanel setMiddleCurrTab={setMiddleCurrTab} />
							</div>
						</Split>
						{isMappingMode && (
							<SimpleMappingConnections 
								saveMappingToRedux={saveMappingToRedux}
								middleCurrTab={middleCurrTab}
								setMiddleCurrTab={setMiddleCurrTab}
							/>
						)}
					</div>
				)}
			</Split>
			{/* {isMappingMode && <MappingNotification />} */}
		</div>
	);
}

export default Outbound;


const findNode = (tree, key) => {
	if (!key) return null;
	let nestedLevels = key?.split("-");
	nestedLevels = nestedLevels?.splice(1);
	function insertNodeInNestedTree(translatedData, nestedLevels, isInside) {
		if (nestedLevels.length === 0) {
		  return translatedData;
		}
  
		const isArray = Array.isArray(translatedData);
		if (isArray) {
		  const nestedData = translatedData[[nestedLevels[0]]];
		  const newNestedLevels = nestedLevels.splice(1);
		  return insertNodeInNestedTree(nestedData, newNestedLevels, isInside);
		} else {
		  // const newNestedLevels = nestedLevels.splice(1);
		  return insertNodeInNestedTree(translatedData.children, nestedLevels, isInside);
		}
	  }
  
	  return insertNodeInNestedTree(tree, nestedLevels);
}